import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Globe,
  File,
  Trash2,
  BarChart3,
} from 'lucide-react';
import { HistoryItem } from '../hooks/useHistoryCache';
import { Button } from '@/ui';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatTime } from '../utils/timeFormatter';

interface HistorySliderProps {
  history: HistoryItem[];
  onSelectItem: (item: HistoryItem) => void;
  onRemoveItem: (id: string) => void;
  className?: string;
}

export const HistorySlider: React.FC<HistorySliderProps> = ({
  history,
  onSelectItem,
  onRemoveItem,
  className = '',
}) => {
  const { t } = useTranslation();
  const [scrollPosition, setScrollPosition] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  if (history.length === 0) return null;

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'url':
        return <Globe className="w-4 h-4" />;
      case 'file':
        return <File className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getModeColor = (mode: string) => {
    switch (mode) {
      case 'url':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
      case 'file':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const getLevelBadge = (level: string) => {
    const levelMap = {
      'ultra-court': {
        label: t('summary.form.levels.ultra_short'),
        color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      },
      'bullet-points': {
        label: t('summary.form.levels.points_cles'),
        color:
          'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
      },
      detaillé: {
        label: t('summary.form.levels.detailed'),
        color:
          'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      },
    };
    return (
      levelMap[level as keyof typeof levelMap] || {
        label: level,
        color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      }
    );
  };

  const scroll = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const scrollAmount = 300;
      const newPosition =
        direction === 'left'
          ? Math.max(0, scrollPosition - scrollAmount)
          : scrollPosition + scrollAmount;

      sliderRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
      setScrollPosition(newPosition);
    }
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  return (
    <div className={`sm:py-4 ${className} overflow-x-auto max-w-[90vw]`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">
            {t('history.title')} ({history.length})
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
          <span>
            {formatTime(
              history.reduce(
                (acc, item) => acc + (item.result.timeSaved || 0),
                0
              )
            )}{' '}
            {t('history.totalSaved')}
          </span>
        </div>
      </div>

      {/* Contenu du slider */}
      <div className="relative" ref={containerRef}>
        {/* Boutons de navigation */}
        {history.length > 2 && (
          <>
            <Button
              size="sm"
              variant="outline"
              className="absolute -left-1 sm:-left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 shadow-lg rounded-full w-7 h-7 sm:w-8 sm:h-8 p-0"
              onClick={() => scroll('left')}
            >
              <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="absolute -right-1 sm:-right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 shadow-lg rounded-full w-7 h-7 sm:w-8 sm:h-8 p-0"
              onClick={() => scroll('right')}
            >
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </>
        )}

        {/* Slider des éléments */}
        <div
          ref={sliderRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide py-2"
          style={{ scrollBehavior: 'smooth' }}
        >
          {history.map((item) => {
            const levelInfo = getLevelBadge(item.level);

            return (
              <div
                key={item.id}
                className="flex-shrink-0 w-64 sm:w-72 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer transition-all group"
                onClick={() => onSelectItem(item)}
              >
                <div className="flex items-start justify-between mb-2 sm:mb-3">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span
                      className={`p-1 sm:p-1.5 rounded ${getModeColor(item.mode)}`}
                    >
                      <div className="w-3 h-3 sm:w-4 sm:h-4">
                        {getModeIcon(item.mode)}
                      </div>
                    </span>
                    <span
                      className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded-full font-medium ${levelInfo.color}`}
                    >
                      {levelInfo.label}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(item.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 sm:p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full transition-all"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />
                  </button>
                </div>

                <div className="space-y-1.5 sm:space-y-2">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                    {item.url
                      ? new URL(item.url).hostname
                      : item.fileName
                        ? item.fileName
                        : truncateText(item.content)}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                    {truncateText(item.result.summary, 60)}
                  </p>
                </div>

                <div className="flex items-center justify-between mt-2 sm:mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                  <span className="text-xs">
                    {formatDistanceToNow(item.timestamp, {
                      addSuffix: true,
                      locale: fr,
                    })}
                  </span>
                  {item.result.timeSaved && (
                    <span className="flex items-center gap-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium text-xs">
                      <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      {formatTime(item.result.timeSaved)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HistorySlider;
